import { ShowErrorModel } from '@bo-client/shared/form-errors/errors-form-model';
import { FormGroup } from '@angular/forms';
import { EventEmitter, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { UploadTypeEnum } from '@bo-client/shared/components/upload-documents/upload-documents.enum';
import { GisDataModel } from '@bo-client/shared/models/gisData';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { TenantDto } from '@bo-client/shared/models/tenantDto';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { AuthenticationService } from '@bo-client/core';
import { ConnectionObject } from '@bo-client/shared/models/connectionObject';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import {
  CONNECTION_OBJECT_AREA_UNAVAILABLE_MESSAGE,
  isConnectionObjectAreaAvailabilityValid
} from '@bo-client/shared/validators/connection-object-area-validation';
import { AllAreaEnum } from '@bo-client/activation-management/shared/models/commissioningModel';

@Injectable()
export abstract class RequestFormBase<T> {
  abstract getAllValidationErrors(): ShowErrorModel[];

  protected constructor(
    protected authorizationService: AuthorizationService,
    protected dataShareService: DataShareService
  ) {}

  public errorFormList: ShowErrorModel[] = [];

  // GIS SPECIFIC ERROR DISPLAY
  public displayMessage: string = null;
  public objectNotFound = false;

  onErrorsChanged = new EventEmitter<any>();

  protected tryToSubmit = false;
  protected compUnsubs$ = new Subject<void>();

  public isDataLoaded = false;
  public isAdminUser = false;
  public selectedTenant: TenantDto;

  /**
   * indicates that the form was successfully initialized
   */
  public initialised: boolean;

  public disable = false;

  // for now we put all shared functionality of GridRequest and ComMissioningRequest here
  // this needs to be moved to another class to keep this clean from technical data
  public selectedGisData: GisDataModel;
  public selectedCompanyLocation: CompanyLocationDto;
  public selectedCompanyLocationid: number;
  public isAreaTypeAvailableInObjectAddress = true;
  public connectionObjectAvailableAreas: unknown[] = [];

  abstract getFormGroup(): FormGroup;

  abstract isValid(): boolean;

  detectFormChanges() {
    this.getFormGroup()
      .valueChanges.pipe(takeUntil(this.compUnsubs$), debounceTime(100), distinctUntilChanged())
      .subscribe(() => {
        this.loadErrorsList();
      });
  }

  uploadDocument(event: UploadTypeEnum) {
    if (event === UploadTypeEnum.uploadAfterDisable) {
      // TODO this.onSubmit('save')
    }
  }

  disableFields() {
    this.disable = true;
    Object.keys(this.getFormGroup().controls).forEach((controlName) => {
      const notDisable = [
        'shortDescription',
        'longDescription',
        'externalId',
        'externalGridRegistrationId',
        'assignedTransformerStation'
      ];
      if (!notDisable.includes(controlName)) {
        this.getFormGroup().controls[controlName]['disable']({ emitEvent: false });
      }
    });
  }

  enableFields() {
    this.disable = false;
    Object.keys(this.getFormGroup().controls).forEach((controlName) => {
      const notDisable = [
        'shortDescription',
        'longDescription',
        'externalId',
        'externalGridRegistrationId',
        'assignedTransformerStation'
      ];
      if (!notDisable.includes(controlName)) {
        this.getFormGroup().controls[controlName]['enable']({ emitEvent: false });
      }
    });
  }

  abstract loadErrorsList();

  abstract getModelFromForm(): T;

  abstract loadFormWithModel(result: T);

  abstract updateFormWithModel(result: T);

  changeGisData(answer: {
    gisData: GisDataModel;
    objectNotFound: boolean;
    areaAvailable: boolean;
    availableAreas?: AllAreaEnum[];
    currentCompanyLocationExternalId?: string;
    currentCompanyLocationFromGis?: CompanyLocationDto;
  }) {
    this.objectNotFound = answer.objectNotFound;
    if (this.objectNotFound) {
      this.displayMessage =
        'Für die Hausnummer wurde kein Adresspunkt gefunden, die Verfügbarkeitsprüfung konnte nicht stattfinden';
    } else {
      this.displayMessage = null;
    }
    this.selectedCompanyLocation = answer.currentCompanyLocationFromGis;
    this.isAreaTypeAvailableInObjectAddress = answer.areaAvailable;
    this.connectionObjectAvailableAreas = this.resolveConnectionObjectAvailableAreas(answer);
    this.selectedGisData = answer.gisData;
  }

  isConnectionObjectAreaAvailabilityValid(): boolean {
    return isConnectionObjectAreaAvailabilityValid(
      this.connectionObjectAvailableAreas,
      this.isAreaTypeAvailableInObjectAddress
    );
  }

  appendConnectionObjectAreaValidationErrors(errors: ShowErrorModel[]): ShowErrorModel[] {
    if (this.isConnectionObjectAreaAvailabilityValid()) {
      return errors;
    }
    const error = new ShowErrorModel();
    error.displayName = 'Anschlussadresse';
    error.controlName = 'connectionObject';
    error.errorMessage = CONNECTION_OBJECT_AREA_UNAVAILABLE_MESSAGE;
    return [...errors, error];
  }

  resetConnectionObjectAreaAvailabilityState(): void {
    this.isAreaTypeAvailableInObjectAddress = true;
    this.connectionObjectAvailableAreas = [];
  }

  private resolveConnectionObjectAvailableAreas(answer: {
    availableAreas?: AllAreaEnum[];
    gisData?: GisDataModel;
  }): unknown[] {
    if (answer.availableAreas?.length) {
      return answer.availableAreas;
    }
    const gisAreas = answer.gisData?.availableAreas;
    return Array.isArray(gisAreas) ? gisAreas : [];
  }

  // TODO: needs a refactoring.. this should be in a component that connects connectionObject and connectionObject Component
  loadGisData(connectionObject: ConnectionObject, uniqueCoId: string): GisDataModel {
    let selectedGisData = null;
    if (connectionObject) {
      selectedGisData = new GisDataModel();
      selectedGisData.city = connectionObject.city;
      selectedGisData.street = connectionObject.street;
      selectedGisData.streetId = connectionObject.street;
      selectedGisData.developmentAreaName = connectionObject.developmentAreaName;
      selectedGisData.corridorNumber = connectionObject.corridorNumber;
      selectedGisData.houseNo = connectionObject.streetNo;
      selectedGisData.houseNoAddition = connectionObject.streetNoAddition;
      selectedGisData.zipCode = connectionObject.zipCode;
      selectedGisData.zipCode = connectionObject.zipCode;
      selectedGisData.uniqueCoId = uniqueCoId;
    }
    return selectedGisData;
  }
}
